import type { UnlikelyProduct } from "@unlikelystudio/commerce-connector"

import type { InferReturn, Nullish } from "~/@types/generics"
import { serializeShopifyImages, type TImage } from "~/components/ui/Image/_data/serializer"
import { linkSchema } from "~/components/ui/Link/_data/schema"
import serializeLink from "~/components/ui/Link/_data/serializer"
import { serializeProductColor } from "~/components/ui/Product/ProductHeader/_data/serialize-product-color"
import { getMediasByMarketName } from "~/components/ui/Product/ProductHeader/_data/utils/get-medias-by-market-name"
import { getHasMultiplePrices } from "~/components/ui/Product/utils/get-has-multiple-prices"
import { getIsBundleProduct } from "~/components/ui/Product/utils/get-is-bundle-product"
import { getProductTitle } from "~/components/ui/Product/utils/get-product-title"
import serializePrice from "~/data/price/serializer"
import type { TProductIntegrationField } from "~/data/products-integration-field/serializer"
import { getProductMetafieldValue } from "~/lib/shopify/constants"
import { getSfMedia } from "~/lib/shopify/queries/get-sf-media"
import { serializeVariants } from "~/lib/shopify/serializers/serialize-variants"
import { handleZodSafeParse } from "~/lib/zod/utils/handle-zod-safe-parse"
import getAssociatedProductsIdsFromProduct from "~/utils/get-associated-products-ids-from-product"

export type TProductCard = InferReturn<typeof serializeProductCard>

export async function serializeProductCard(
  product: UnlikelyProduct,
  locale: Nullish<string>,
  additionalProduct?: Nullish<TProductIntegrationField>
) {
  const parsedLink = handleZodSafeParse(linkSchema, product, {
    errorContext: `serializeProductCard: ${product.handle}`,
    locale,
  })

  const variants = serializeVariants(product, product.variants, locale)

  // Check if product is bundle
  const isBundle = getIsBundleProduct(product)

  const customImageId = getProductMetafieldValue(product.metafields, "CUSTOM_DEFAULT_IMG")

  const [customImageResponse, associatedProductsIds, color] = await Promise.all([
    customImageId ? getSfMedia({ withLocale: false, id: customImageId }) : null,
    getAssociatedProductsIdsFromProduct(product, locale),
    serializeProductColor(product, locale, false),
  ])

  const customImage = customImageResponse?.media ?? null

  const hasMultiplePrices = getHasMultiplePrices({ isProductBundle: isBundle, product })

  const serializedCustomImage = serializeShopifyImages([customImage])?.[0] ?? null

  const imagesByMarketName = getMediasByMarketName(product.images, locale)
  const images = serializeShopifyImages(imagesByMarketName ?? [])

  const computedImage = [
    ...(additionalProduct?.image ? [additionalProduct?.image] : []),
    ...(additionalProduct?.imageHover ? [additionalProduct?.imageHover] : []),
    ...(serializedCustomImage ? [serializedCustomImage] : []),
    ...images,
  ] satisfies TImage[]

  const link = parsedLink ? serializeLink(parsedLink, locale, false) : null

  // Remove the current product
  const associatedProductsColorsLength = associatedProductsIds?.filter((id) => id !== product.id)?.length ?? 0

  return {
    id: product.id,
    title: additionalProduct?.title || getProductTitle(product),
    subtitle: additionalProduct?.subtitle || getProductMetafieldValue(product.metafields, "SUBTITLE"),
    collections: product?.collections ?? [],
    color,
    link,
    priceRange: product.priceRange,
    price: serializePrice({
      locale,
      amount: product.priceRange?.minVariantPrice?.amount,
      currencyCode: product.priceRange?.minVariantPrice?.currencyCode,
    }),
    hasMultiplePrices,
    compareAtPrice:
      (product.compareAtPriceRange?.minVariantPrice?.amount ?? 0) > 0
        ? serializePrice({
          locale,
          amount: product.compareAtPriceRange?.minVariantPrice?.amount,
          currencyCode: product.compareAtPriceRange?.minVariantPrice?.currencyCode,
        })
        : null,
    availableForSale: product.availableForSale,
    images: computedImage.slice(0, 3),
    tag: additionalProduct?.hideTag
      ? null
      : additionalProduct?.tag || getProductMetafieldValue(product.metafields, "TAG"),
    colorsLength: associatedProductsColorsLength,
    // For tracking in serializeViewedItemListPayload
    collection: product.collections.at(-1) ?? null,
    // For tracking in serializeViewedItemListPayload
    variants,
  }
}
